import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [booting, setBooting] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    // Auto-login bridge: BeyondTheTable can launch the portal with ?token=JWT
    const params = new URLSearchParams(window.location.search);
    const incoming = params.get("token");
    if (incoming) {
      setToken(incoming);
      params.delete("token");
      const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", clean);
    }
    loadMe();
    // Safety net: never let the splash hang if a request stalls.
    const t = setTimeout(() => {
      setBooting(false);
      setUser((prev) => (prev === null ? false : prev));
    }, 9000);
    return () => clearTimeout(t);
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, username, password) => {
    const { data } = await api.post("/auth/register", { email, username, password });
    if (data.token) setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async (credential) => {
    const { data } = await api.post("/auth/google", { credential });
    if (data.token) setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    localStorage.removeItem("token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, booting, login, register, loginWithGoogle, logout, refresh: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
